import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { posApi, formatApiError } from '@/lib/api';
import ActivateSubscriptionDialog from '@/components/shared/ActivateSubscriptionDialog';
import { CameraScanBox } from '@/components/shared/CameraScanBox';
import { useScanMode } from '@/hooks/useScanMode';
import { toast } from 'sonner';
import { ArrowLeft, Search, QrCode, Camera, ScanLine, ChevronUp } from 'lucide-react';

export default function PosPendingPayments() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [activatingId, setActivatingId] = useState(null);
  const [q, setQ] = useState('');

  // Scanner is minimized by default — it only opens when the cashier taps "Scan QR".
  const [scanOpen, setScanOpen] = useState(false);
  const [scanMode, setScanMode] = useScanMode();
  const [scanSession, setScanSession] = useState(0);
  const inputRef = useRef(null);
  const handledRef = useRef(false);

  const load = () =>
    posApi.get('/subscriptions', { params: { status: 'PENDING' } }).then(({ data }) => setSubscriptions(data.subscriptions));

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (scanOpen && scanMode === 'SCANNER') {
      handledRef.current = false;
      inputRef.current?.focus();
    }
  }, [scanOpen, scanMode, scanSession]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return subscriptions;
    return subscriptions.filter((s) => {
      const name = (s.customer?.name || '').toLowerCase();
      const phone = (s.customer?.phone || '').toLowerCase();
      const code = (s.customer?.customerCode || '').toLowerCase();
      return name.includes(term) || phone.includes(term) || code.includes(term);
    });
  }, [subscriptions, q]);

  const handleScan = async (token) => {
    try {
      const { data } = await posApi.post('/qr/scan', { token });
      const match = subscriptions.find((s) => s.customer?.id === data.customer.id);
      if (match) {
        setScanOpen(false);
        setActivatingId(match.id);
      } else {
        toast.error(`${data.customer.name} has no pending payment.`);
        setScanSession((n) => n + 1);
        handledRef.current = false;
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setScanSession((n) => n + 1);
      handledRef.current = false;
    }
  };

  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const token = e.target.value.trim();
    e.target.value = '';
    if (!token || handledRef.current) return;
    handledRef.current = true;
    handleScan(token);
  };

  const toggleScan = () => {
    setScanOpen((open) => {
      const next = !open;
      if (next) setScanSession((n) => n + 1);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-obsidian px-6 py-8 flex flex-col items-center">
      <div className="w-full max-w-md flex items-center gap-3 mb-6">
        <button data-testid="pos-pending-back-btn" onClick={() => navigate('/pos/home')} className="text-white/60 hover:text-white">
          <ArrowLeft size={22} />
        </button>
        <h1 className="font-heading text-xl font-semibold text-white">Pending Payments</h1>
      </div>

      <div className="w-full max-w-md space-y-3">
        {/* Search by name / phone */}
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-ink" />
          <input
            data-testid="pos-pending-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or phone"
            className="w-full h-12 rounded-xl pl-11 pr-4 bg-white text-charcoal"
          />
        </div>

        {/* Scan QR — collapsed by default, opens the scanner only when tapped */}
        <button
          data-testid="pos-pending-scan-toggle-btn"
          onClick={toggleScan}
          className="w-full h-12 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center gap-2 font-medium transition-colors duration-150"
        >
          {scanOpen ? <ChevronUp size={18} /> : <QrCode size={18} />}
          {scanOpen ? 'Hide Scanner' : 'Scan QR to Find Customer'}
        </button>

        {scanOpen && (
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="pos-pending-scanner">
            <div className="p-2 flex items-center justify-center gap-1 border-b border-white/10">
              <button
                data-testid="pos-pending-scan-mode-camera-btn"
                onClick={() => setScanMode('CAMERA')}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-150 ${
                  scanMode === 'CAMERA' ? 'bg-saffron text-white' : 'text-white/50 hover:bg-white/10'
                }`}
              >
                <Camera size={14} /> Camera
              </button>
              <button
                data-testid="pos-pending-scan-mode-scanner-btn"
                onClick={() => setScanMode('SCANNER')}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-150 ${
                  scanMode === 'SCANNER' ? 'bg-saffron text-white' : 'text-white/50 hover:bg-white/10'
                }`}
              >
                <ScanLine size={14} /> Handheld Scanner
              </button>
            </div>

            {scanMode === 'CAMERA' ? (
              <CameraScanBox onScan={handleScan} scanSession={scanSession} />
            ) : (
              <div className="p-10 flex flex-col items-center text-center gap-3">
                <ScanLine size={44} className="text-saffron" />
                <p className="text-white/70 text-sm">Ready — scan the customer&apos;s QR code with your handheld scanner</p>
                <input
                  ref={inputRef}
                  data-testid="pos-pending-hardware-scanner-input"
                  autoFocus
                  onKeyDown={onKeyDown}
                  className="opacity-0 h-1 w-1 absolute"
                />
              </div>
            )}
          </div>
        )}

        {/* Pending list */}
        <div className="space-y-2 pt-1">
          {filtered.map((s) => (
            <div key={s.id} data-testid="pos-pending-row" className="bg-white/10 rounded-xl p-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-white font-medium">{s.customer.name}</p>
                <p className="text-white/50 text-sm">
                  {s.customer.phone} · ${Number(s.finalAmount).toFixed(2)}
                </p>
              </div>
              <button
                data-testid={`pos-activate-btn-${s.id}`}
                onClick={() => setActivatingId(s.id)}
                className="h-10 px-4 rounded-lg bg-saffron hover:bg-saffron-hover text-white text-sm font-medium transition-colors duration-150"
              >
                Activate
              </button>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-white/40 text-sm text-center pt-6">
              {q.trim() ? 'No matching pending payments.' : 'No pending payments right now.'}
            </p>
          )}
        </div>
      </div>

      {activatingId && (
        <ActivateSubscriptionDialog
          api={posApi}
          subscriptionId={activatingId}
          onClose={() => setActivatingId(null)}
          onDone={() => {
            setActivatingId(null);
            load();
          }}
        />
      )}
    </div>
  );
}
